import {useSyncExternalStore} from 'react';
import {trackedByHolmes} from './holmesThinkingStore';
import {trackFenzSafeFunnelEvent} from './fenzsafeFunnelEvents';

const THINKING_DELAY_MS = 220;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function mutate<T>(work: () => T): Promise<T> {
  return trackedByHolmes(
    (async () => {
      await delay(THINKING_DELAY_MS);
      return work();
    })(),
  );
}

export class FenzSafeConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FenzSafeConflictError';
  }
}

/**
 * Category and status vocabulary carried forward from the validated PR #108
 * prototype (prototypes/fenzsafe-organizer/organizer.js) rather than
 * reinvented -- that prototype's interaction model (matter creation,
 * filename-only fixtures, category/status triage, timeline, CSV export) is
 * the founder-approved starting point per the 2026-09-11 directive.
 */
export const DOCUMENT_CATEGORIES = ['Court notice', 'Pleadings', 'Correspondence', 'Evidence', 'Research', 'Other'] as const;
export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

export const TRIAGE_STATES = ['Unsorted', 'Reviewed', 'Filed'] as const;
export type TriageState = (typeof TRIAGE_STATES)[number];

/** Same upload-lifecycle vocabulary as ProFenz's CompanyFileDocument (carolinaWorkflowStore.ts) -- one vocabulary for "is this file usable" across both products, not a fifth invention. 'awaiting_upload' matches the real backend's FenzSafeDocumentProcessingStatus::AWAITING_UPLOAD (Issue #114 founder-delegated re-review, comment 5724759851): a document row exists but no bytes have been registered for it yet, so it must never be presented as accepted. */
export type DocumentProcessingStatus = 'awaiting_upload' | 'accepted' | 'pending_scan' | 'quarantined' | 'duplicate' | 'too_large' | 'unavailable';

/** DocumentController::MAX_BYTES -- enforced server-side; checked here first so a too-large file is rejected before ever reaching the network. */
export const FENZSAFE_DOCUMENT_MAX_BYTES = 10485760;

export type EvidenceRelevance = 'relevant' | 'not_relevant' | 'needs_review';

export type FenzSafeDocument = {
  id: string;
  name: string;
  category: DocumentCategory;
  triageState: TriageState;
  processingStatus: DocumentProcessingStatus;
  sizeKb: number;
  addedAt: string;
  source: 'upload' | 'phone_import' | 'desktop_discovery';
  /** Only meaningful for source: 'phone_import' -- a message/photo pulled from a large export needs a relevance decision before it's treated as part of the matter. */
  evidenceRelevance?: EvidenceRelevance;
  /** Only meaningful for source: 'phone_import' -- the device/export the customer said this came from (e.g. "iPhone 14 -- Messages export, Sept 9, 2026"), entered once and carried onto every item pulled from that same export. Lets evidence be grouped by source and gives the plain-language provenance note something real to point at. */
  phoneSource?: string;
  supersededBy?: string;
  docVersion: number;
  /** Backend row concurrency token, distinct from content lineage and matter.version.
   * Synthetic adapters omit it because they use the matter aggregate token. */
  rowVersion?: number;
  /** Set when this document was uploaded as a replacement for an earlier one -- lets the UI show "version 2 of Notice of hearing.pdf" and link back. */
  replacesDocumentId?: string;
  /** The document's real, already-persisted SHA-256 digest, from DocumentController::serialize()'s 'content_sha256' field -- undefined whenever there's nothing genuine to show yet (processingStatus isn't 'accepted', or the backend has no storage collaborator wired). Never a placeholder/synthetic value. */
  contentSha256?: string;
};

export const EVIDENCE_REQUEST_STATUSES = ['pending', 'fulfilled', 'declined'] as const;
export type EvidenceRequestStatus = (typeof EVIDENCE_REQUEST_STATUSES)[number];

/**
 * One item an attorney has asked for through a FenzSafe share link (Issue #164 founder direction,
 * 2026-09-22: "the attorney could review and state, I also need the text messages you stated you
 * had ... the request goes back to the client"). Mirrors ProFenz\FenzSafe\FenzSafeEvidenceRequest.
 */
export type EvidenceRequest = {
  id: string;
  description: string;
  status: EvidenceRequestStatus;
  fulfilledDocumentId?: string;
  declineReason?: string;
  createdAt: string;
  resolvedAt?: string;
  version: number;
};

/**
 * A metadata-only, non-authoritative filename-keyword suggestion for one Unsorted document
 * (Issue #192 Docket #17 "Organize my documents", Stage B/C). Mirrors the wire shape returned by
 * `GET /api/v1/fenzsafe/matters/{matterId}/organize-suggestions`
 * (DocumentOrganizationSuggestionService::suggestForMatter()) -- never persisted, never implies a
 * legal/relevance conclusion, and never applied without the human explicitly accepting or editing
 * it (see FenzSafeApp.tsx's review panel).
 */
export type OrganizeSuggestion = {
  documentId: string;
  currentCategory: DocumentCategory;
  suggestedCategory: DocumentCategory;
  reason: string;
};

/** Mirrors ProFenz\FenzSafe\MatterWatchTriggerType exactly -- the founder's Matter Watch spec (Issue #114): "complaint, summons, subpoena, eviction, domestication trigger types". */
export const MATTER_WATCH_TRIGGER_TYPES = ['complaint', 'summons', 'subpoena', 'eviction_notice', 'domestication'] as const;
export type MatterWatchTriggerType = (typeof MATTER_WATCH_TRIGGER_TYPES)[number];

/**
 * One matter's Matter Watch subscription (Issue #114 founder spec; backend: MatterWatchRepository/
 * PdoMatterWatchRepository). At most one subscription per matter -- null means never subscribed.
 * `version` is this subscription's own optimistic-concurrency token, distinct from the parent
 * Matter's `version` (same relationship as EvidenceRequest.version to Matter.version above).
 */
export type MatterWatchSubscription = {
  partyNames: string[];
  court: string;
  county: string;
  caseType?: string;
  caseNumber?: string;
  triggerTypes: MatterWatchTriggerType[];
  enabled: boolean;
  createdAt: string;
  version: number;
};

export type TimelineEntry = {
  id: string;
  label: string;
  detail?: string;
  at: string;
};

export type MatterTier = 'free' | 'one_time' | 'monthly';

export type AttorneyPackageStatus = 'not_created' | 'draft' | 'shared' | 'opened' | 'revoked';

export type AttorneyPackage = {
  status: AttorneyPackageStatus;
  /** Backend package concurrency token; synthetic adapters use matter.version. */
  rowVersion?: number;
  shareLink?: string;
  createdAt?: string;
  openedAt?: string;
  revokedAt?: string;
  /** The document ids currently frozen into the shared snapshot (Issue #114, Slice C document-selection UI) -- undefined/empty when no package has ever been created. Never silently includes a document that wasn't explicitly selected. */
  sharedDocumentIds?: string[];
};

/**
 * In a real system this comes from the signed-in account, not a question asked every time --
 * an attorney or paralegal's account already knows what they are. This preview has no real
 * accounts, so it's captured once at matter creation as the honest stand-in: it's what lets
 * Holmes Jr. decide whether to explain legal vocabulary (a pro se litigant) or skip the
 * explanation (someone who already works with these terms professionally).
 */
/** Deprecated wire compatibility only; it is not a truthful representation status. */
export type FenzSafeUserRole = 'pro_se' | 'attorney_or_paralegal';
export type FenzSafeRepresentationStatus = 'represented' | 'seeking_counsel' | 'self_represented';

export type Matter = {
  id: string;
  name: string;
  reference: string;
  createdAt: string;
  tier: MatterTier;
  archived: boolean;
  documents: FenzSafeDocument[];
  timeline: TimelineEntry[];
  attorneyPackage: AttorneyPackage;
  evidenceRequests: EvidenceRequest[];
  /** Current, unreviewed organize-suggestions for this matter's Unsorted documents -- recomputed fresh on every load/refresh, never stored between them. */
  organizeSuggestions: OrganizeSuggestion[];
  /** null until the customer subscribes -- at most one Matter Watch subscription per matter. */
  matterWatch: MatterWatchSubscription | null;
  /** No real email/push delivery exists yet -- this only changes what the synthetic timeline records when the attorney opens the package, so Codex has a labeled preference to wire a real notifier against. */
  notifyOnAttorneyOpen: boolean;
  userRole: FenzSafeUserRole | null;
  representationStatus: FenzSafeRepresentationStatus | null;
  version: number;
};

/**
 * Set only by the real backend adapter, when the initial/refresh load of a customer's matters
 * itself failed -- distinct from a per-mutation error (which the calling screen already surfaces
 * inline via its own error state). 'sign_in_required' means the backend reported no session at
 * all (FenzSafeSignInRequired server-side); 'unavailable' covers every other load failure
 * (network error, a generic denial, a 5xx). The synthetic store never sets this -- its own load
 * can't fail -- so it's optional rather than every adapter having to fabricate a value.
 */
export type FenzSafeLoadError = {type: 'sign_in_required' | 'unavailable'; message: string};

export type FenzSafeMatterCreationSummary = {
  available: boolean;
  used: number;
  limit: number | null;
  reason: 'available' | 'included_matter_used' | 'policy_unavailable';
};

export type FenzSafeState = {
  matters: Matter[];
  loadError?: FenzSafeLoadError;
  creationSummary?: FenzSafeMatterCreationSummary;
};

let sequence = 100;

function initialState(): FenzSafeState {
  return {
    // The synthetic walkthrough is intentionally unrestricted. The persisted adapter replaces
    // this with the server-governed summary returned by MatterController::list().
    creationSummary: {available: true, used: 1, limit: null, reason: 'available'},
    matters: [
      {
        id: 'FS-LOCAL-1',
        name: 'Landlord dispute — Cedar Ridge Apartments',
        reference: 'FS-2026-0001',
        createdAt: 'Sept 8, 2026 · 6:40 PM ET',
        tier: 'free',
        archived: false,
        version: 1,
        documents: [
          {id: 'FSD-1', name: 'Notice of hearing.pdf', category: 'Court notice', triageState: 'Reviewed', processingStatus: 'accepted', sizeKb: 340, addedAt: 'Sept 8, 2026 · 6:42 PM ET', source: 'upload', docVersion: 1},
          {id: 'FSD-2', name: 'Client timeline notes.txt', category: 'Correspondence', triageState: 'Unsorted', processingStatus: 'accepted', sizeKb: 12, addedAt: 'Sept 8, 2026 · 6:44 PM ET', source: 'upload', docVersion: 1},
          {id: 'FSD-3', name: 'lease-agreement-signed.pdf', category: 'Pleadings', triageState: 'Filed', processingStatus: 'accepted', sizeKb: 890, addedAt: 'Sept 8, 2026 · 6:50 PM ET', source: 'upload', docVersion: 1},
          {id: 'FSD-4', name: 'text-export-thread-14.json', category: 'Evidence', triageState: 'Unsorted', processingStatus: 'accepted', sizeKb: 2400, addedAt: 'Sept 9, 2026 · 8:15 AM ET', source: 'phone_import', evidenceRelevance: 'needs_review', phoneSource: 'iPhone 14 — Messages export, Sept 9, 2026', docVersion: 1},
          {id: 'FSD-5', name: 'photo-moldy-ceiling.heic', category: 'Evidence', triageState: 'Unsorted', processingStatus: 'pending_scan', sizeKb: 3100, addedAt: 'Sept 9, 2026 · 8:16 AM ET', source: 'phone_import', evidenceRelevance: 'relevant', phoneSource: 'iPhone 14 — Messages export, Sept 9, 2026', docVersion: 1},
        ],
        timeline: [
          {id: 'T1', label: 'Matter created', at: 'Sept 8, 2026 · 6:40 PM ET'},
          {id: 'T2', label: 'Notice of hearing.pdf added', at: 'Sept 8, 2026 · 6:42 PM ET'},
          {id: 'T3', label: 'Client timeline notes.txt added', at: 'Sept 8, 2026 · 6:44 PM ET'},
          {id: 'T4', label: 'lease-agreement-signed.pdf added and filed', at: 'Sept 8, 2026 · 6:50 PM ET'},
          {id: 'T5', label: 'Phone export reviewed for relevant evidence', at: 'Sept 9, 2026 · 8:16 AM ET'},
        ],
        attorneyPackage: {status: 'not_created'},
        evidenceRequests: [],
        matterWatch: null,
        // The synthetic walkthrough never runs the real keyword heuristic (that would be a second,
        // parallel organizer model -- Issue #192 Docket #17 Stage A boundary) -- it always starts
        // with no suggestions.
        organizeSuggestions: [],
        notifyOnAttorneyOpen: true,
        userRole: 'pro_se',
        representationStatus: null,
      },
    ],
  };
}

let state: FenzSafeState = initialState();
const listeners = new Set<() => void>();
/** Synthetic-only in-memory byte custody -- see syntheticFenzSafeStore.registerDocumentBytes()'s doc comment. Never persisted past this browser tab; reset() below clears it along with everything else. */
let syntheticDocumentBytes = new Map<string, File>();
/** Docket B12 in-memory per-matter guard for `first_value_reached` (see hasReachedFirstValue()'s own
 * doc comment below): that predicate is a live recomputation from current triage state, not a
 * sticky flag, so a document reviewed then reverted to Unsorted then reviewed again would otherwise
 * cross the false-to-true edge twice for the same matter. updateDocument() below seeds this from
 * the matter's live pre-mutation state (not just this flag) so a matter already past first value
 * when first observed in a fresh session -- freshly loaded from the backend, or from an earlier
 * browser runtime -- is never mistaken for a brand-new transition by a later non-transition edit.
 *
 * Honest limitation (this is module memory, not backend-persisted state, since no migration/backend
 * change is in scope here): this guard, and the seeding above, only see this one browser runtime.
 * Two tabs/devices open on the same matter at once can each independently observe and fire the
 * transition if their local view of "before" hasn't yet picked up what the other one just wrote to
 * the backend. The metric is at most one observed transition per matter while this guard survives.
 * Reload/store reset clears it; if reviewed documents were reverted/removed before that reset,
 * pre-mutation seeding cannot recover the lost history and a later transition may emit again.
 * This is not cross-device, server-enforced lifetime uniqueness. Document edits alone never clear
 * this guard; the explicit reset paths below do. */
let firstValueReachedMatterIds = new Set<string>();

function emit() {
  for (const listener of listeners) listener();
}

function requireMatter(matterId: string): Matter {
  const matter = state.matters.find((item) => item.id === matterId);
  if (!matter) throw new Error(`Unknown matter: ${matterId}`);
  return matter;
}

function setMatter(matterId: string, expectedVersion: number, update: Partial<Matter>, event?: {label: string; detail?: string}): Matter {
  const current = requireMatter(matterId);
  if (current.version !== expectedVersion) {
    // Mirrors the real backend's own conflict message exactly (MatterController::mutation()'s
    // catch block) -- this used to read "FS-LOCAL-2 changed since it was loaded (expected version
    // 1, currently 2). Reload before retrying.", a raw internal id plus developer-log phrasing that
    // friendlyMutationError() (Issue #114 recovery-hardening finding) passes through unchanged for
    // any real Error, never softened by its fallback argument.
    throw new FenzSafeConflictError('The matter changed since it was loaded. Reload and try again.');
  }
  sequence += 1;
  const timeline = event ? [...current.timeline, {id: `T${sequence}`, label: event.label, detail: event.detail, at: 'Just now (synthetic)'}] : current.timeline;
  const next: Matter = {...current, ...update, timeline, version: current.version + 1};
  state = {...state, matters: state.matters.map((item) => (item.id === matterId ? next : item))};
  emit();
  return next;
}

export interface FenzSafeAdapter {
  subscribe(listener: () => void): () => void;
  getSnapshot(): FenzSafeState;
  reset(): void;
  createMatter(name: string, reference: string, representationStatus: FenzSafeRepresentationStatus | null, idempotencyKey: string): Promise<string>;
  addDocument(matterId: string, expectedVersion: number, input: {name: string; category: DocumentCategory; source: 'upload' | 'phone_import' | 'desktop_discovery'; sizeKb: number; phoneSource?: string}, idempotencyKey: string): Promise<Matter>;
  /**
   * Sends a document's actual selected bytes to real storage -- the second half of "choose a
   * file" (addDocument() creates the metadata row at AWAITING_UPLOAD; this call is what can ever
   * advance it past that). Works equally on a brand-new document or an existing AWAITING_UPLOAD
   * stub created by the name-only "add by name" affordance -- FenzSafeDocumentProcessingStatus
   * allows AWAITING_UPLOAD -> PENDING_SCAN regardless of how the row was created. Resolves once
   * the scan outcome (accepted/quarantined) or a scanner-unavailable/storage error is known; the
   * caller reads the final state off the returned Matter's documents, it is never inferred here.
   */
  registerDocumentBytes(matterId: string, expectedVersion: number, documentId: string, file: File, idempotencyKey: string): Promise<Matter>;
  /** Downloads a document's real, already-accepted bytes. Throws for any document not currently accepted -- there is nothing real to download otherwise. */
  downloadDocumentBytes(documentId: string, signal?: AbortSignal): Promise<Blob>;
  /**
   * Re-fetches this matter's true current documents/attorney-package/timeline. Every other
   * mutation above returns the post-mutation Matter on success, but registerDocumentBytes()
   * throws (never returns a Matter) on every failure path -- a network failure, a scanner-
   * unavailable 503, a rejected/quarantined scan, or a version conflict -- even though a
   * quarantined or scanner-unavailable outcome is a real, already-durable server-side state
   * change, not merely an aborted attempt. Without this, the caller's local cache keeps showing
   * the document at whatever it was before the failed attempt (e.g. still "awaiting_upload" after
   * it was actually quarantined) until an unrelated refresh happens to occur. Callers use this to
   * resync just the one affected matter after catching a registerDocumentBytes() failure, so the
   * UI can show the document's real post-failure status and the matching recovery action.
   */
  refreshMatter(matterId: string): Promise<Matter>;
  addDocumentVersion(matterId: string, documentId: string, expectedVersion: number, input: {name: string; sizeKb: number}, idempotencyKey: string): Promise<Matter>;
  updateDocument(matterId: string, documentId: string, expectedVersion: number, patch: Partial<Pick<FenzSafeDocument, 'name' | 'category' | 'triageState' | 'evidenceRelevance'>>, idempotencyKey: string): Promise<Matter>;
  removeDocument(matterId: string, documentId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter>;
  renameMatter(matterId: string, expectedVersion: number, input: {name: string; reference: string}, idempotencyKey: string): Promise<Matter>;
  /**
   * Creates the attorney share for a matter from an explicit document selection (Issue #114,
   * Slice C document-selection UI) -- documentIds must be non-empty; the caller (FenzSafeApp.tsx)
   * never auto-selects every matter document, and an adapter must never silently substitute one.
   */
  createAttorneyPackage(matterId: string, expectedVersion: number, documentIds: string[], idempotencyKey: string): Promise<Matter>;
  simulateAttorneyOpen(matterId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter>;
  revokeAttorneyPackage(matterId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter>;
  /** Explicit "Update shared package" / re-snapshot action -- replaces membership wholesale without touching the share token/status. Never called implicitly by an ordinary matter/document change. */
  updateAttorneyPackageSnapshot(matterId: string, expectedVersion: number, documentIds: string[], idempotencyKey: string): Promise<Matter>;
  updateNotificationPreference(matterId: string, expectedVersion: number, notifyOnAttorneyOpen: boolean, idempotencyKey: string): Promise<Matter>;
  updateRepresentationStatus(matterId: string, expectedVersion: number, representationStatus: FenzSafeRepresentationStatus | null, idempotencyKey: string): Promise<Matter>;
  upgradeTier(matterId: string, expectedVersion: number, tier: MatterTier, idempotencyKey: string): Promise<Matter>;
  archiveMatter(matterId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter>;
  restoreMatter(matterId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter>;
  /** Attaches an existing, already-accepted document as what the attorney asked for. requestExpectedVersion is the evidence request's own version (not the matter's), matching the real backend's per-request optimistic concurrency. */
  fulfillEvidenceRequest(matterId: string, requestId: string, requestExpectedVersion: number, documentId: string, idempotencyKey: string): Promise<Matter>;
  /** No reason is ever required -- a pro se litigant should never be blocked from moving on just because they can't explain why something isn't available. */
  declineEvidenceRequest(matterId: string, requestId: string, requestExpectedVersion: number, reason: string | undefined, idempotencyKey: string): Promise<Matter>;
  /** Creates this matter's one Matter Watch subscription. Rejected if a subscription already exists (MatterWatchRepository::subscribe()'s own uniqueness rule) -- callers must unsubscribe first to replace one. */
  subscribeMatterWatch(
    matterId: string,
    input: {partyNames: string[]; court: string; county: string; caseType?: string; caseNumber?: string; triggerTypes: MatterWatchTriggerType[]},
    idempotencyKey: string,
  ): Promise<Matter>;
  /** watchExpectedVersion is the subscription's own version (mirrors requestExpectedVersion above), not the parent Matter's. */
  updateMatterWatch(
    matterId: string,
    watchExpectedVersion: number,
    patch: Partial<{partyNames: string[]; court: string; county: string; caseType: string | null; caseNumber: string | null; triggerTypes: MatterWatchTriggerType[]; enabled: boolean}>,
    idempotencyKey: string,
  ): Promise<Matter>;
  unsubscribeMatterWatch(matterId: string, watchExpectedVersion: number, idempotencyKey: string): Promise<Matter>;
  /**
   * Reports a completed "organize my documents" suggestion-review session (Issue #192 Docket #17
   * Stage C) as a durable timeline summary. Individual accept/edit actions are already durable the
   * moment they happen, via the existing updateDocument() category patch above -- this call only
   * records the aggregate accepted/edited/skipped counts for the session; it never stores
   * suggestion content itself (mirrors DocumentController::completeOrganizeSuggestions()).
   */
  completeOrganizeSuggestions(matterId: string, accepted: number, edited: number, skipped: number, idempotencyKey: string): Promise<Matter>;
}

const syntheticFenzSafeStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot(): FenzSafeState {
    return state;
  },
  reset() {
    state = initialState();
    sequence = 100;
    syntheticDocumentBytes = new Map();
    emit();
  },

  createMatter(name: string, reference: string, representationStatus: FenzSafeRepresentationStatus | null, idempotencyKey: string): Promise<string> {
    return mutate(() => {
      void idempotencyKey;
      sequence += 1;
      const id = `FS-LOCAL-${sequence}`;
      const matter: Matter = {
        id,
        name,
        reference: reference || `FS-2026-${String(sequence).padStart(4, '0')}`,
        createdAt: 'Just now (synthetic)',
        tier: 'free',
        archived: false,
        version: 1,
        documents: [],
        timeline: [{id: `T${sequence}`, label: 'Matter created', at: 'Just now (synthetic)'}],
        attorneyPackage: {status: 'not_created'},
        evidenceRequests: [],
        matterWatch: null,
        organizeSuggestions: [],
        notifyOnAttorneyOpen: true,
        userRole: null,
        representationStatus,
      };
      state = {
        ...state,
        matters: [...state.matters, matter],
        creationSummary: state.creationSummary ? {...state.creationSummary, used: state.matters.length + 1} : undefined,
      };
      emit();
      return id;
    });
  },

  addDocument(matterId: string, expectedVersion: number, input: {name: string; category: DocumentCategory; source: 'upload' | 'phone_import' | 'desktop_discovery'; sizeKb: number; phoneSource?: string}, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      if (current.version !== expectedVersion) {
        // Same fix as setMatter()'s own conflict message above -- this is a redundant pre-check
        // (setMatter() below would catch it too) that used to throw its own raw, unfriendly text.
        throw new FenzSafeConflictError('The matter changed since it was loaded. Reload and try again.');
      }
      sequence += 1;
      const doc: FenzSafeDocument = {
        id: `FSD-${sequence}`,
        name: input.name,
        category: input.category,
        triageState: 'Unsorted',
        processingStatus: 'awaiting_upload',
        sizeKb: input.sizeKb,
        addedAt: 'Just now (synthetic)',
        source: input.source,
        evidenceRelevance: input.source === 'phone_import' ? 'needs_review' : undefined,
        phoneSource: input.source === 'phone_import' ? input.phoneSource : undefined,
        docVersion: 1,
      };
      return setMatter(matterId, expectedVersion, {documents: [...current.documents, doc]}, {label: `${input.name} added`, detail: input.source === 'phone_import' ? `From ${input.phoneSource || 'a phone export'} — needs a relevance review.` : undefined});
    });
  },

  /**
   * Synthetic-only byte "registration": keeps the selected File in memory for this browser tab
   * only (syntheticDocumentBytes below), simulates a clean scan, and transitions the document
   * straight to accepted -- there is no real storage, encryption, or scanning happening here, only
   * enough of the real two-call shape (add() then registerDocumentBytes()) for the walkthrough to
   * exercise the same UI flow FenzSafeApp.tsx uses against the real backend. Never represented as
   * real byte custody: nothing here is durable, encrypted, or scanned by a real AV engine.
   */
  registerDocumentBytes(matterId: string, expectedVersion: number, documentId: string, file: File, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      const target = current.documents.find((doc) => doc.id === documentId);
      if (!target) throw new Error(`Unknown document: ${documentId}`);
      syntheticDocumentBytes.set(documentId, file);
      const documents = current.documents.map((doc) => (doc.id === documentId ? {...doc, processingStatus: 'accepted' as const} : doc));
      return setMatter(matterId, expectedVersion, {documents}, {label: `${target.name} uploaded and passed a (synthetic) security scan`});
    });
  },

  /** Returns the in-memory File kept by registerDocumentBytes() above -- synthetic-only, never persisted past this browser tab. */
  downloadDocumentBytes(documentId: string): Promise<Blob> {
    return mutate(() => {
      const file = syntheticDocumentBytes.get(documentId);
      if (!file) throw new Error('No document bytes are available to download in this preview.');
      return file;
    });
  },

  /** Synthetic state is always self-consistent -- registerDocumentBytes() above never leaves it stale on failure the way the real adapter's HTTP calls can. This exists only to satisfy FenzSafeAdapter's interface with the matter's real current (already up to date) state. */
  refreshMatter(matterId: string): Promise<Matter> {
    return mutate(() => requireMatter(matterId));
  },

  /** Uploads a replacement for an existing document -- the prior version is kept (marked superseded), never overwritten in place, so the timeline and any already-shared attorney package still resolve. */
  addDocumentVersion(matterId: string, documentId: string, expectedVersion: number, input: {name: string; sizeKb: number}, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      const target = current.documents.find((doc) => doc.id === documentId);
      if (!target) throw new Error(`Unknown document: ${documentId}`);
      if (target.supersededBy) throw new Error(`${target.name} already has a newer version.`);
      sequence += 1;
      const newDoc: FenzSafeDocument = {
        id: `FSD-${sequence}`,
        name: input.name,
        category: target.category,
        triageState: 'Unsorted',
        processingStatus: 'awaiting_upload',
        sizeKb: input.sizeKb,
        addedAt: 'Just now (synthetic)',
        source: target.source,
        docVersion: target.docVersion + 1,
        replacesDocumentId: target.id,
      };
      const documents = current.documents.map((doc) => (doc.id === documentId ? {...doc, supersededBy: newDoc.id} : doc)).concat(newDoc);
      return setMatter(matterId, expectedVersion, {documents}, {label: `${input.name} added as version ${newDoc.docVersion} of ${target.name}`});
    });
  },

  updateDocument(matterId: string, documentId: string, expectedVersion: number, patch: Partial<Pick<FenzSafeDocument, 'name' | 'category' | 'triageState' | 'evidenceRelevance'>>, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      const target = current.documents.find((doc) => doc.id === documentId);
      if (!target) throw new Error(`Unknown document: ${documentId}`);
      const updatedDocs = current.documents.map((doc) => (doc.id === documentId ? {...doc, ...patch} : doc));
      const label = patch.triageState ? `${target.name} marked ${patch.triageState.toLowerCase()}` : patch.evidenceRelevance ? `${target.name} marked ${patch.evidenceRelevance.replace('_', ' ')}` : `${target.name} updated`;
      return setMatter(matterId, expectedVersion, {documents: updatedDocs}, {label});
    });
  },

  /**
   * Removes a document the customer added by mistake. Refuses if a newer version has already
   * superseded it -- deleting a historical version out from under its successor would corrupt the
   * "Verified document history" chain the app promises is immutable ("kept, not overwritten").
   * If the removed document itself replaced an earlier one, that earlier version is restored to
   * current (its supersededBy cleared) rather than left dangling.
   */
  removeDocument(matterId: string, documentId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      const target = current.documents.find((doc) => doc.id === documentId);
      if (!target) throw new Error(`Unknown document: ${documentId}`);
      if (target.supersededBy) throw new Error(`${target.name} has a newer version and can't be removed -- remove the newer version first.`);
      const documents = current.documents.filter((doc) => doc.id !== documentId).map((doc) => (doc.id === target.replacesDocumentId ? {...doc, supersededBy: undefined} : doc));
      return setMatter(matterId, expectedVersion, {documents}, {label: `${target.name} removed`});
    });
  },

  /** Corrects a matter's name/reference after creation -- a typo at intake shouldn't be permanent. */
  renameMatter(matterId: string, expectedVersion: number, input: {name: string; reference: string}, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const name = input.name.trim();
      if (!name) throw new Error('Matter name cannot be empty.');
      return setMatter(matterId, expectedVersion, {name, reference: input.reference.trim()}, {label: 'Matter name/reference updated'});
    });
  },

  createAttorneyPackage(matterId: string, expectedVersion: number, documentIds: string[], idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      if (documentIds.length === 0) throw new Error('Select at least one document before creating an attorney package.');
      return setMatter(
        matterId,
        expectedVersion,
        {attorneyPackage: {status: 'shared', shareLink: `https://fenzsafe.example.invalid/share/${idempotencyKey.slice(0, 8)}`, createdAt: 'Just now (synthetic)', sharedDocumentIds: [...documentIds]}},
        {label: `Attorney package created and share link generated (${documentIds.length} document${documentIds.length === 1 ? '' : 's'} shared)`},
      );
    });
  },

  /** Synthetic-only: stands in for a real attorney-link-open webhook/analytics event until one exists. */
  simulateAttorneyOpen(matterId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      if (current.attorneyPackage.status !== 'shared' && current.attorneyPackage.status !== 'opened') throw new Error('No attorney package has been shared yet.');
      return setMatter(
        matterId,
        expectedVersion,
        {attorneyPackage: {...current.attorneyPackage, status: 'opened', openedAt: 'Just now (synthetic)'}},
        {label: 'Attorney opened the shared package', detail: current.notifyOnAttorneyOpen ? 'You would have been emailed here — no real email is sent in this preview.' : undefined},
      );
    });
  },

  /** Revokes a shared or opened package's link. The prior link stops resolving (FenzSafeAttorneyView treats 'revoked' as unavailable); creating a new package afterward issues a fresh link. */
  revokeAttorneyPackage(matterId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      if (current.attorneyPackage.status !== 'shared' && current.attorneyPackage.status !== 'opened') throw new Error('No shared package to revoke.');
      return setMatter(
        matterId,
        expectedVersion,
        {attorneyPackage: {...current.attorneyPackage, status: 'revoked', revokedAt: 'Just now (synthetic)'}},
        {label: 'Attorney package access revoked', detail: 'The previous share link no longer opens.'},
      );
    });
  },

  /** Explicit re-snapshot: replaces which documents are shared without touching the share link/status -- matches the real backend's updateSnapshot() action never issuing a fresh token. */
  updateAttorneyPackageSnapshot(matterId: string, expectedVersion: number, documentIds: string[], idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      if (current.attorneyPackage.status !== 'shared' && current.attorneyPackage.status !== 'opened') throw new Error('No shared package to update.');
      if (documentIds.length === 0) throw new Error('Select at least one document before updating the shared package.');
      return setMatter(
        matterId,
        expectedVersion,
        {attorneyPackage: {...current.attorneyPackage, sharedDocumentIds: [...documentIds]}},
        {label: `Shared package updated (${documentIds.length} document${documentIds.length === 1 ? '' : 's'} shared)`},
      );
    });
  },

  updateNotificationPreference(matterId: string, expectedVersion: number, notifyOnAttorneyOpen: boolean, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      return setMatter(matterId, expectedVersion, {notifyOnAttorneyOpen}, {label: notifyOnAttorneyOpen ? 'Turned on attorney-open email notifications' : 'Turned off attorney-open email notifications'});
    });
  },

  updateRepresentationStatus(matterId: string, expectedVersion: number, representationStatus: FenzSafeRepresentationStatus | null, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      if (current.archived) throw new FenzSafeConflictError('The matter changed since it was loaded. Reload and try again.');
      return setMatter(matterId, expectedVersion, {representationStatus}, {label: 'Matter representation status updated'});
    });
  },

  upgradeTier(matterId: string, expectedVersion: number, tier: MatterTier, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      return setMatter(matterId, expectedVersion, {tier}, {label: `Upgraded to ${tier === 'one_time' ? 'one-time prep pass' : 'monthly case workspace'}`});
    });
  },

  archiveMatter(matterId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      return setMatter(matterId, expectedVersion, {archived: true}, {label: 'Matter archived'});
    });
  },

  restoreMatter(matterId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      return setMatter(matterId, expectedVersion, {archived: false}, {label: 'Matter restored from archive'});
    });
  },

  fulfillEvidenceRequest(matterId: string, requestId: string, requestExpectedVersion: number, documentId: string, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      const target = current.evidenceRequests.find((request) => request.id === requestId);
      if (!target) throw new Error('This request could not be found.');
      if (target.status !== 'pending' || target.version !== requestExpectedVersion) throw new FenzSafeConflictError('This request changed since it was loaded. Reload and try again.');
      const document = current.documents.find((item) => item.id === documentId);
      if (!document || document.processingStatus !== 'accepted') throw new Error('That document has not finished processing yet.');
      const evidenceRequests = current.evidenceRequests.map((request) =>
        request.id === requestId ? {...request, status: 'fulfilled' as const, fulfilledDocumentId: documentId, resolvedAt: 'Just now (synthetic)', version: request.version + 1} : request,
      );
      return setMatter(current.id, current.version, {evidenceRequests}, {label: `Provided "${document.name}" for something the attorney requested`});
    });
  },

  declineEvidenceRequest(matterId: string, requestId: string, requestExpectedVersion: number, reason: string | undefined, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      const target = current.evidenceRequests.find((request) => request.id === requestId);
      if (!target) throw new Error('This request could not be found.');
      if (target.status !== 'pending' || target.version !== requestExpectedVersion) throw new FenzSafeConflictError('This request changed since it was loaded. Reload and try again.');
      const evidenceRequests = current.evidenceRequests.map((request) =>
        request.id === requestId ? {...request, status: 'declined' as const, declineReason: reason, resolvedAt: 'Just now (synthetic)', version: request.version + 1} : request,
      );
      return setMatter(current.id, current.version, {evidenceRequests}, {label: 'Declined something the attorney requested'});
    });
  },

  completeOrganizeSuggestions(matterId: string, accepted: number, edited: number, skipped: number, idempotencyKey: string): Promise<Matter> {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      return setMatter(current.id, current.version, {}, {label: 'Document-organization suggestions reviewed', detail: `${accepted} accepted, ${edited} edited, ${skipped} skipped`});
    });
  },

  subscribeMatterWatch(matterId, input, idempotencyKey) {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      if (current.matterWatch !== null) throw new FenzSafeConflictError('A Matter Watch subscription already exists for this matter.');
      if (input.partyNames.length === 0) throw new Error('At least one party name is required.');
      if (input.court.trim() === '' || input.county.trim() === '') throw new Error('Court and county are required.');
      if (input.triggerTypes.length === 0) throw new Error('At least one trigger type is required.');
      const matterWatch: MatterWatchSubscription = {
        partyNames: input.partyNames,
        court: input.court,
        county: input.county,
        caseType: input.caseType,
        caseNumber: input.caseNumber,
        triggerTypes: input.triggerTypes,
        enabled: true,
        createdAt: 'Just now (synthetic)',
        version: 1,
      };
      return setMatter(current.id, current.version, {matterWatch}, {label: 'Started watching this matter for new filings'});
    });
  },

  updateMatterWatch(matterId, watchExpectedVersion, patch, idempotencyKey) {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      const target = current.matterWatch;
      if (!target) throw new Error('No Matter Watch subscription exists for this matter.');
      if (target.version !== watchExpectedVersion) throw new FenzSafeConflictError('The Matter Watch subscription changed since it was loaded. Reload and try again.');
      const matterWatch: MatterWatchSubscription = {
        ...target,
        ...patch,
        caseType: patch.caseType === undefined ? target.caseType : patch.caseType ?? undefined,
        caseNumber: patch.caseNumber === undefined ? target.caseNumber : patch.caseNumber ?? undefined,
        version: target.version + 1,
      };
      return setMatter(current.id, current.version, {matterWatch}, {label: 'enabled' in patch ? (patch.enabled ? 'Turned Matter Watch back on' : 'Paused Matter Watch') : 'Updated Matter Watch criteria'});
    });
  },

  unsubscribeMatterWatch(matterId, watchExpectedVersion, idempotencyKey) {
    return mutate(() => {
      void idempotencyKey;
      const current = requireMatter(matterId);
      if (!current.matterWatch) throw new Error('No Matter Watch subscription exists for this matter.');
      if (current.matterWatch.version !== watchExpectedVersion) throw new FenzSafeConflictError('The Matter Watch subscription changed since it was loaded. Reload and try again.');
      return setMatter(current.id, current.version, {matterWatch: null}, {label: 'Stopped watching this matter'});
    });
  },
} satisfies FenzSafeAdapter;

let activeFenzSafeAdapter: FenzSafeAdapter = syntheticFenzSafeStore;
let usingRealBackend = false;

/**
 * Selects which FenzSafeAdapter implementation the `fenzsafeStore` binding below actually calls
 * through. FenzSafeApp.tsx/FenzSafeAttorneyView.tsx/FenzSafeWalkthrough.tsx import and call
 * `fenzsafeStore.<method>()` directly (e.g. `fenzsafeStore.createMatter(...)`), never through a
 * prop, so switching between the local synthetic implementation (the loopback-only walkthrough)
 * and the real backend (fenzsafeBackendAdapter.ts, once a real ProFenz session exists) has to
 * happen here rather than by changing those components. Call this once, before FenzSafeApp first
 * mounts on the real (non-walkthrough) route -- see FenzSafeWorkspace.tsx.
 */
export function setFenzSafeAdapter(adapter: FenzSafeAdapter): void {
  activeFenzSafeAdapter = adapter;
  usingRealBackend = true;
}

/**
 * Whether `fenzsafeStore` currently calls through to the real backend (setFenzSafeAdapter() has
 * been called) rather than the local synthetic module (the loopback-only walkthrough, or a guest
 * visitor at /fenzsafe/try -- see resetGuestFenzSafeStore()). FenzSafeApp.tsx uses this to decide
 * which of its two "is anything really saved/billed" messages to show; it must reflect the actual
 * adapter binding, not merely "not previewing as an attorney" (every synthetic-module render is
 * also "not previewing as an attorney").
 */
export function isUsingRealFenzSafeBackend(): boolean {
  return usingRealBackend;
}

/**
 * Whether `fenzsafeStore` calls actually reach the real backend adapter, as opposed to
 * `isUsingRealFenzSafeBackend()`'s presentational flag, which `setUsingRealFenzSafeBackendForTests`
 * can flip without touching `activeFenzSafeAdapter`. A caller that is about to bypass the adapter
 * and issue its own network request directly (Docket #37 Stage 1c's chunked-upload client, which
 * `fetch`es its own upload-session endpoints instead of going through `activeFenzSafeAdapter`) must
 * check this, not the presentational flag, or it will fire real HTTP requests against the synthetic
 * walkthrough/guest preview and every test that opts into the real-backend banner via
 * `setUsingRealFenzSafeBackendForTests(true)`.
 */
export function isFenzSafeAdapterReal(): boolean {
  return activeFenzSafeAdapter !== syntheticFenzSafeStore;
}

/**
 * Test-only. A handful of FenzSafeApp.tsx tests need to render against the synthetic adapter's
 * data (so the seeded matter/documents are there to assert on) while still exercising the
 * real-backend banner copy -- calling setFenzSafeAdapter() itself would swap in a different
 * adapter and lose that seeded data. Never call this from production code.
 */
export function setUsingRealFenzSafeBackendForTests(value: boolean): void {
  usingRealBackend = value;
}

/**
 * Test-only. Simulates a fresh page load's empty `first_value_reached` guard (see
 * `firstValueReachedMatterIds`'s own doc comment) without touching the matters/documents already
 * loaded -- exactly the real-world gap a page reload creates against a backend matter that already
 * has history. Calling `fenzsafeStore.reset()` instead would also wipe that matter data, which is
 * the opposite of what this scenario needs to exercise. Never call this from production code.
 */
export function clearFirstValueReachedGuardForTests(): void {
  firstValueReachedMatterIds = new Set();
}

/**
 * Resets to the synthetic adapter with a genuinely blank slate -- no pre-seeded matter -- for the
 * real, production-reachable guest entry point (FenzSafeGuestEntry.tsx). syntheticFenzSafeStore's
 * own reset()/initialState() intentionally pre-seed one demo matter for the loopback-only
 * walkthrough (FenzSafeWalkthrough.tsx) so there's always something to click through; a real,
 * unauthenticated visitor must never see someone else's invented case data presented as already
 * theirs. Bypasses the shared FenzSafeAdapter.reset() contract deliberately, since that contract's
 * one existing implementer (fenzsafeBackendAdapter.ts) has no notion of "empty vs. seeded" and this
 * is only ever called against the synthetic adapter.
 */
export function resetGuestFenzSafeStore(): void {
  activeFenzSafeAdapter = syntheticFenzSafeStore;
  usingRealBackend = false;
  state = {creationSummary: {available: true, used: 0, limit: null, reason: 'available'}, matters: []};
  sequence = 100;
  syntheticDocumentBytes = new Map();
  firstValueReachedMatterIds = new Set();
  emit();
}

export const fenzsafeStore: FenzSafeAdapter = {
  subscribe: (listener) => activeFenzSafeAdapter.subscribe(listener),
  getSnapshot: () => activeFenzSafeAdapter.getSnapshot(),
  reset: () => { firstValueReachedMatterIds.clear(); activeFenzSafeAdapter.reset(); },
  createMatter: async (name, reference, representationStatus, idempotencyKey) => {
    const result = await activeFenzSafeAdapter.createMatter(name, reference, representationStatus, idempotencyKey);
    trackFenzSafeFunnelEvent({milestone: 'matter_created'});
    return result;
  },
  addDocument: async (matterId, expectedVersion, input, idempotencyKey) => {
    const result = await activeFenzSafeAdapter.addDocument(matterId, expectedVersion, input, idempotencyKey);
    trackFenzSafeFunnelEvent({milestone: 'evidence_added', source: input.source});
    return result;
  },
  registerDocumentBytes: (matterId, expectedVersion, documentId, file, idempotencyKey) => activeFenzSafeAdapter.registerDocumentBytes(matterId, expectedVersion, documentId, file, idempotencyKey),
  downloadDocumentBytes: (documentId, signal) => activeFenzSafeAdapter.downloadDocumentBytes(documentId, signal),
  refreshMatter: (matterId) => activeFenzSafeAdapter.refreshMatter(matterId),
  addDocumentVersion: (matterId, documentId, expectedVersion, input, idempotencyKey) => activeFenzSafeAdapter.addDocumentVersion(matterId, documentId, expectedVersion, input, idempotencyKey),
  updateDocument: async (matterId, documentId, expectedVersion, patch, idempotencyKey) => {
    // Seed from the live pre-mutation snapshot, not just the guard flag: on a fresh page load
    // (guard empty) a backend matter can already be past first value from an earlier session.
    // Without this, the next call here -- even a non-transition edit -- would misread that as a
    // brand-new transition. Seeding from the matter's actual state right before this mutation
    // closes that gap while the guard itself still stops the same matter re-firing later in this
    // session after a reversal (Reviewed -> Unsorted -> Reviewed), which a live before/after
    // comparison alone cannot do since it only ever looks one mutation back.
    const before = activeFenzSafeAdapter.getSnapshot().matters.find((matter) => matter.id === matterId);
    if (before && hasReachedFirstValue(before)) firstValueReachedMatterIds.add(matterId);
    const result = await activeFenzSafeAdapter.updateDocument(matterId, documentId, expectedVersion, patch, idempotencyKey);
    if (!firstValueReachedMatterIds.has(matterId) && hasReachedFirstValue(result)) {
      firstValueReachedMatterIds.add(matterId);
      trackFenzSafeFunnelEvent({milestone: 'first_value_reached'});
    }
    return result;
  },
  removeDocument: (matterId, documentId, expectedVersion, idempotencyKey) => activeFenzSafeAdapter.removeDocument(matterId, documentId, expectedVersion, idempotencyKey),
  renameMatter: (matterId, expectedVersion, input, idempotencyKey) => activeFenzSafeAdapter.renameMatter(matterId, expectedVersion, input, idempotencyKey),
  createAttorneyPackage: async (matterId, expectedVersion, documentIds, idempotencyKey) => {
    const result = await activeFenzSafeAdapter.createAttorneyPackage(matterId, expectedVersion, documentIds, idempotencyKey);
    // organizer_completed and attorney_shared necessarily fire together here -- there is no
    // separate "mark organizing complete" action in this UI distinct from creating the share
    // itself (docs/ai/FENZSAFE_P2P_FUNNEL_INSTRUMENTATION.md §3).
    trackFenzSafeFunnelEvent({milestone: 'organizer_completed'});
    trackFenzSafeFunnelEvent({milestone: 'attorney_shared'});
    return result;
  },
  simulateAttorneyOpen: (matterId, expectedVersion, idempotencyKey) => activeFenzSafeAdapter.simulateAttorneyOpen(matterId, expectedVersion, idempotencyKey),
  revokeAttorneyPackage: (matterId, expectedVersion, idempotencyKey) => activeFenzSafeAdapter.revokeAttorneyPackage(matterId, expectedVersion, idempotencyKey),
  updateAttorneyPackageSnapshot: (matterId, expectedVersion, documentIds, idempotencyKey) => activeFenzSafeAdapter.updateAttorneyPackageSnapshot(matterId, expectedVersion, documentIds, idempotencyKey),
  updateNotificationPreference: (matterId, expectedVersion, notifyOnAttorneyOpen, idempotencyKey) => activeFenzSafeAdapter.updateNotificationPreference(matterId, expectedVersion, notifyOnAttorneyOpen, idempotencyKey),
  updateRepresentationStatus: (matterId, expectedVersion, representationStatus, idempotencyKey) => activeFenzSafeAdapter.updateRepresentationStatus(matterId, expectedVersion, representationStatus, idempotencyKey),
  upgradeTier: async (matterId, expectedVersion, tier, idempotencyKey) => {
    const result = await activeFenzSafeAdapter.upgradeTier(matterId, expectedVersion, tier, idempotencyKey);
    // A downgrade to the free tier is not a paid conversion.
    if (tier === 'one_time' || tier === 'monthly') {
      trackFenzSafeFunnelEvent({milestone: 'paid_conversion', tier});
    }
    return result;
  },
  archiveMatter: (matterId, expectedVersion, idempotencyKey) => activeFenzSafeAdapter.archiveMatter(matterId, expectedVersion, idempotencyKey),
  restoreMatter: (matterId, expectedVersion, idempotencyKey) => activeFenzSafeAdapter.restoreMatter(matterId, expectedVersion, idempotencyKey),
  fulfillEvidenceRequest: (matterId, requestId, requestExpectedVersion, documentId, idempotencyKey) =>
    activeFenzSafeAdapter.fulfillEvidenceRequest(matterId, requestId, requestExpectedVersion, documentId, idempotencyKey),
  declineEvidenceRequest: (matterId, requestId, requestExpectedVersion, reason, idempotencyKey) =>
    activeFenzSafeAdapter.declineEvidenceRequest(matterId, requestId, requestExpectedVersion, reason, idempotencyKey),
  completeOrganizeSuggestions: (matterId, accepted, edited, skipped, idempotencyKey) =>
    activeFenzSafeAdapter.completeOrganizeSuggestions(matterId, accepted, edited, skipped, idempotencyKey),
  subscribeMatterWatch: (matterId, input, idempotencyKey) => activeFenzSafeAdapter.subscribeMatterWatch(matterId, input, idempotencyKey),
  updateMatterWatch: (matterId, watchExpectedVersion, patch, idempotencyKey) => activeFenzSafeAdapter.updateMatterWatch(matterId, watchExpectedVersion, patch, idempotencyKey),
  unsubscribeMatterWatch: (matterId, watchExpectedVersion, idempotencyKey) => activeFenzSafeAdapter.unsubscribeMatterWatch(matterId, watchExpectedVersion, idempotencyKey),
};

export function useFenzSafe(): FenzSafeState {
  return useSyncExternalStore(fenzsafeStore.subscribe, fenzsafeStore.getSnapshot);
}

export function triageCounts(matter: Matter): {total: number; unsorted: number; filed: number; needsRelevanceReview: number} {
  return {
    total: matter.documents.length,
    unsorted: matter.documents.filter((doc) => doc.triageState === 'Unsorted').length,
    filed: matter.documents.filter((doc) => doc.triageState === 'Filed').length,
    needsRelevanceReview: matter.documents.filter((doc) => doc.evidenceRelevance === 'needs_review').length,
  };
}

/**
 * Docket item B12 (Issue #192 comment 5860340094, closing B3 Finding D): a distinct, truthful
 * "first value moment" signal -- every new document starts `triageState: 'Unsorted'`
 * (fenzsafeStore.ts addDocument()), so a document actually reaching 'Reviewed' or 'Filed' means the
 * user genuinely reviewed and corrected something, not merely uploaded it. This is the real stage-5
 * milestone Issue #190's canonical journey names distinctly from stage-4 `evidence_added`.
 *
 * This is a live recomputation from the matter's current triage state, not a sticky flag: it can
 * go true, then false again (a reviewed document reverted to Unsorted), then true again. The caller
 * suppresses repeated `first_value_reached` events only while its in-memory guard survives; see
 * `firstValueReachedMatterIds` above for reset/reload limits. This function remains a snapshot of "is this
 * matter past first value right now," since other callers (e.g. the guest banner's progress copy) need
 * that live truth rather than a one-shot latch.
 */
export function hasReachedFirstValue(matter: Matter): boolean {
  const counts = triageCounts(matter);
  return counts.unsorted < counts.total;
}

/** Groups a matter's documents by category, in the fixed DOCUMENT_CATEGORIES order, dropping empty categories. Shared between the customer-side packet preview and the attorney's read-only view so both present the same package identically. */
export function groupDocumentsByCategory(matter: Matter): {category: DocumentCategory; documents: FenzSafeDocument[]}[] {
  return DOCUMENT_CATEGORIES.map((category) => ({category, documents: matter.documents.filter((doc) => doc.category === category)})).filter((group) => group.documents.length > 0);
}

/** Groups phone-imported evidence by the device/export the customer said it came from, most-recently-added source first. A document with no source label (older data, or a skipped field) lands under a single "Unlabeled export" bucket rather than being silently dropped. */
export function groupPhoneEvidenceBySource(matter: Matter): {source: string; documents: FenzSafeDocument[]}[] {
  const phoneDocuments = matter.documents.filter((doc) => doc.source === 'phone_import');
  const order: string[] = [];
  const bySource = new Map<string, FenzSafeDocument[]>();
  for (const doc of phoneDocuments) {
    const key = doc.phoneSource?.trim() || 'Unlabeled export';
    if (!bySource.has(key)) {
      order.push(key);
      bySource.set(key, []);
    }
    bySource.get(key)!.push(doc);
  }
  return order.map((source) => ({source, documents: bySource.get(source)!}));
}

/** Walks a document's complete version chain (v1 through the latest), regardless of which version's id is passed in -- a document only points one hop forward (supersededBy) and one hop back (replacesDocumentId), so this follows both directions to assemble the continuous history the "Verified document history" panel shows. */
export function documentVersionChain(matter: Matter, documentId: string): FenzSafeDocument[] {
  const byId = new Map(matter.documents.map((doc) => [doc.id, doc]));
  let earliest = byId.get(documentId);
  if (!earliest) return [];
  while (earliest.replacesDocumentId) {
    const previous = byId.get(earliest.replacesDocumentId);
    if (!previous) break;
    earliest = previous;
  }
  const chain: FenzSafeDocument[] = [];
  let node: FenzSafeDocument | undefined = earliest;
  while (node) {
    chain.push(node);
    node = node.supersededBy ? byId.get(node.supersededBy) : undefined;
  }
  return chain;
}

export type MatterReadinessFlag = {id: string; message: string};

/** Plain-language "before you share" checklist -- what an attorney-ready package should not still have pending. Read-only: it never blocks package creation, it just tells the customer what's still unresolved so nothing important gets missed. */
export function matterReadinessFlags(matter: Matter): MatterReadinessFlag[] {
  const counts = triageCounts(matter);
  const flags: MatterReadinessFlag[] = [];
  if (counts.total === 0) {
    flags.push({id: 'no-documents', message: 'No documents have been added yet.'});
    return flags;
  }
  if (counts.needsRelevanceReview > 0) {
    flags.push({id: 'needs-review', message: `${counts.needsRelevanceReview} item${counts.needsRelevanceReview === 1 ? '' : 's'} from a phone export still ${counts.needsRelevanceReview === 1 ? 'needs' : 'need'} a relevance decision.`});
  }
  const awaitingUpload = matter.documents.filter((doc) => doc.processingStatus === 'awaiting_upload').length;
  if (awaitingUpload > 0) {
    flags.push({id: 'awaiting-upload', message: `${awaitingUpload} document${awaitingUpload === 1 ? '' : 's'} still ${awaitingUpload === 1 ? 'needs' : 'need'} a file attached before it can be shared.`});
  }
  const stillScanning = matter.documents.filter((doc) => doc.processingStatus === 'pending_scan').length;
  if (stillScanning > 0) {
    flags.push({id: 'scanning', message: `${stillScanning} document${stillScanning === 1 ? '' : 's'} ${stillScanning === 1 ? 'is' : 'are'} still being scanned and can't be sorted yet.`});
  }
  if (counts.unsorted > 0) {
    flags.push({id: 'unsorted', message: `${counts.unsorted} document${counts.unsorted === 1 ? '' : 's'} ${counts.unsorted === 1 ? 'is' : 'are'} still unsorted.`});
  }
  return flags;
}

/** Same CSV-injection guard as the validated PR #108 prototype -- a leading =/+/-/@ in a cell can execute as a formula when opened in Excel/Sheets. */
export function csvCell(value: string): string {
  const spreadsheetSafe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${spreadsheetSafe.replaceAll('"', '""')}"`;
}

/**
 * `isRealBackend` mirrors FenzSafeApp.tsx's own flag (its only signal for whether it's mounted
 * against a real, persisted matter or the loopback-only synthetic walkthrough -- see that
 * component's doc comment). Without it, this always claimed "Synthetic FenzSafe document index"
 * at the top, including for a real customer's real, persisted matter -- an exported CSV of your
 * own real documents falsely labeled as fixture data (Issue #114 recovery-hardening finding).
 */
export function documentIndexCsv(matter: Matter, isRealBackend: boolean): string {
  const rows = [isRealBackend ? 'FenzSafe document index' : 'Synthetic FenzSafe document index', `Matter,${csvCell(matter.name)}`, `Reference,${csvCell(matter.reference)}`, 'Name,Category,Status,Added'];
  for (const doc of matter.documents) {
    rows.push([doc.name, doc.category, doc.triageState, doc.addedAt].map(csvCell).join(','));
  }
  return rows.join('\n');
}
